import { getGrid, type GridGeometry } from '../../grid';
import type { MapDefinition } from '../../model/gamestate/GameState';
import { getMapObjectVisualDefinition, reportMapObjectImageLoadFailure } from '../../content/objectVisualRegistry';
import { RenderBoard, type BoardChunk, type WorldRect } from './board';
import { subscribeToMapObjectImageUpdates } from './objectImages';
import { drawResolvedMapObjectSprite, resolveMapObjectSpriteMetrics, resolveMapObjectWorldAnchor, type MapObjectSpriteInput } from './mapObjectSprites';
import type { Viewport } from './viewport';

const OBJECT_CHUNK_TILE_SIZE = 16;
const OBJECT_CHUNK_PRELOAD = 1;

function rectsIntersect(a: WorldRect, b: WorldRect): boolean {
    return !(a.right < b.left || a.bottom < b.top || a.left > b.right || a.top > b.bottom);
}

export class StaticObjectLayerRenderer {
    private readonly tileSize: number;
    private readonly requestRender: () => void;
    private readonly objectCanvas: HTMLCanvasElement;
    private readonly animatedOverlay: HTMLDivElement;
    private readonly objectCtx: CanvasRenderingContext2D | null;
    private readonly unsubscribeMapObjectImageUpdates: () => void;
    private grid: GridGeometry;
    private map: MapDefinition;
    private board: RenderBoard;
    private devicePixelRatio = 1;
    private viewportWidth = 1;
    private viewportHeight = 1;
    private readonly chunkTextures = new Map<string, HTMLCanvasElement>();
    private readonly chunkObjects = new Map<string, MapObjectSpriteInput[]>();
    private animatedObjects: MapObjectSpriteInput[] = [];
    private readonly dirtyChunkKeys = new Set<string>();
    constructor(
        tileSize: number,
        map: MapDefinition,
        objectCanvas: HTMLCanvasElement,
        animatedOverlay: HTMLDivElement,
        requestRender: () => void
    ) {
        this.tileSize = tileSize;
        this.requestRender = requestRender;
        this.objectCanvas = objectCanvas;
        this.animatedOverlay = animatedOverlay;
        this.objectCtx = objectCanvas.getContext('2d');
        this.map = map;
        this.grid = getGrid(map.gridType);
        this.board = new RenderBoard(map, this.grid, this.tileSize, OBJECT_CHUNK_TILE_SIZE);
        if (this.objectCtx) {
            this.objectCtx.imageSmoothingEnabled = false;
        }
        this.rebuildChunkIndex();
        this.unsubscribeMapObjectImageUpdates = subscribeToMapObjectImageUpdates(() => {
            this.rebuildChunkIndex();
            this.invalidateAllChunks();
        });
    }

    public setMap(map: MapDefinition): void {
        const previousMap = this.map;
        const previousGrid = this.grid;
        const gridChanged = previousMap.gridType !== map.gridType
            || previousMap.width !== map.width
            || previousMap.height !== map.height;

        this.map = map;
        this.grid = getGrid(map.gridType);
        this.board = new RenderBoard(map, this.grid, this.tileSize, OBJECT_CHUNK_TILE_SIZE);

        if (gridChanged) {
            this.chunkTextures.clear();
            this.rebuildChunkIndex();
            this.invalidateAllChunks();
            return;
        }

        const dirtyChunkKeys = this.collectChangedChunkKeys(previousMap.objects, map.objects, previousGrid);
        this.rebuildChunkIndex();
        this.invalidateChunkKeys(dirtyChunkKeys);
    }

    public syncCanvasDimensions(viewportWidth: number, viewportHeight: number): void {
        this.viewportWidth = Math.max(1, Math.round(viewportWidth));
        this.viewportHeight = Math.max(1, Math.round(viewportHeight));
        this.devicePixelRatio =
            typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
                ? Math.max(1, window.devicePixelRatio)
                : 1;
        this.objectCanvas.style.width = `${this.viewportWidth}px`;
        this.objectCanvas.style.height = `${this.viewportHeight}px`;
        this.objectCanvas.width = Math.max(1, Math.round(this.viewportWidth * this.devicePixelRatio));
        this.objectCanvas.height = Math.max(1, Math.round(this.viewportHeight * this.devicePixelRatio));
        this.objectCanvas.style.transform = 'translate3d(0, 0, 0)';
        this.animatedOverlay.style.width = `${this.viewportWidth}px`;
        this.animatedOverlay.style.height = `${this.viewportHeight}px`;
        if (this.objectCtx) {
            this.objectCtx.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
            this.objectCtx.imageSmoothingEnabled = false;
        }

        this.requestRender();
    }

    public draw(viewport: Viewport): void {
        if (!this.objectCtx) {
            return;
        }

        const visibleChunks = this.board.getVisibleChunks(viewport, OBJECT_CHUNK_PRELOAD, 0);
        this.rebuildVisibleDirtyChunks(visibleChunks);

        this.objectCtx.clearRect(0, 0, this.viewportWidth, this.viewportHeight);
        const offset = viewport.getCameraOffset();
        for (const chunk of visibleChunks) {
            const texture = this.chunkTextures.get(chunk.key);
            if (!texture) {
                continue;
            }
            this.objectCtx.drawImage(
                texture,
                chunk.worldBounds.left + offset.x,
                chunk.worldBounds.top + offset.y,
                chunk.worldBounds.width,
                chunk.worldBounds.height,
            );
        }

        this.renderAnimatedOverlays(offset, viewport);
    }

    public hasAnimatedObjects(): boolean {
        return false;
    }

    public destroy(): void {
        this.unsubscribeMapObjectImageUpdates();
        this.animatedOverlay.replaceChildren();
    }

    private rebuildChunkIndex(): void {
        this.chunkObjects.clear();
        this.animatedObjects = [];
        for (const object of this.map.objects) {
            const visualId = object.asset ?? object.id;
            const definition = getMapObjectVisualDefinition(visualId);
            if (definition?.animated) {
                this.animatedObjects.push(object);
                continue;
            }
            for (const key of this.getObjectChunkKeys(object, this.grid)) {
                const bucket = this.chunkObjects.get(key);
                if (bucket) {
                    bucket.push(object);
                } else {
                    this.chunkObjects.set(key, [object]);
                }
            }
        }
    }

    private renderAnimatedOverlays(offset: { x: number; y: number }, _viewport: Viewport): void {
        const visibleIds = new Set<string>();
        for (const object of this.animatedObjects) {
            const definition = getMapObjectVisualDefinition(object.asset ?? object.id);
            if (!definition) {
                continue;
            }
            const metrics = this.resolveAnimatedOverlayMetrics(object, definition);
            if (!metrics) {
                continue;
            }
            const anchor = resolveMapObjectWorldAnchor(object, this.tileSize, this.grid);
            const centerX = anchor.x + offset.x;
            const centerY = anchor.y + offset.y + metrics.spriteCenterY;
            const left = centerX - metrics.drawWidth / 2;
            const top = centerY - metrics.drawHeight / 2;

            if (
                left + metrics.drawWidth < 0
                || top + metrics.drawHeight < 0
                || left > this.viewportWidth
                || top > this.viewportHeight
            ) {
                continue;
            }

            const element = this.getOrCreateAnimatedOverlayElement(object, definition);
            element.style.display = 'block';
            element.style.width = `${Math.round(metrics.drawWidth)}px`;
            element.style.height = `${Math.round(metrics.drawHeight)}px`;
            const transformEffects = this.buildAnimatedOverlayTransform(object);
            const base = `translate3d(${left}px, ${top}px, 0)`;
            element.style.transform = transformEffects ? `${base} ${transformEffects}` : base;
            element.style.opacity = '1';
            visibleIds.add(object.id);
        }

        const children = this.animatedOverlay.children;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (!(child instanceof HTMLImageElement)) {
                continue;
            }
            if (!visibleIds.has(child.dataset.objectId ?? '')) {
                child.style.display = 'none';
            }
        }
    }

    private getOrCreateAnimatedOverlayElement(
        object: MapObjectSpriteInput,
        definition: NonNullable<ReturnType<typeof getMapObjectVisualDefinition>>,
    ): HTMLImageElement {
        const existing = this.animatedOverlay.querySelector(`img[data-object-id="${CSS.escape(object.id)}"]`);
        if (existing instanceof HTMLImageElement) {
            existing.alt = definition.label;
            if (existing.src !== new URL(definition.imagePath, window.location.href).href) {
                existing.src = definition.imagePath;
            }

            return existing;
        }

        const image = document.createElement('img');
        image.dataset.objectId = object.id;
        image.alt = definition.label;
        image.draggable = false;
        image.decoding = 'async';
        image.src = definition.imagePath;
        image.style.position = 'absolute';
        image.style.left = '0px';
        image.style.top = '0px';
        image.style.pointerEvents = 'none';
        image.style.transformOrigin = 'center';
        image.style.imageRendering = 'pixelated';
        image.style.display = 'none';
        image.onerror = () => {
            reportMapObjectImageLoadFailure(object.asset ?? object.id, definition.imagePath);
            image.style.display = 'none';
        };
        this.animatedOverlay.appendChild(image);
        return image;
    }

    private resolveAnimatedOverlayMetrics(
        object: MapObjectSpriteInput,
        definition: NonNullable<ReturnType<typeof getMapObjectVisualDefinition>>,
    ): { drawWidth: number; drawHeight: number; spriteCenterY: number } | null {
        const sourceWidth = Math.max(1, definition.width ?? 64);
        const sourceHeight = Math.max(1, definition.height ?? 64);
        const aspect = sourceWidth / sourceHeight;
        const maxDrawSize = this.tileSize * definition.drawScale * Math.max(0.01, object.sizeMultiplier ?? 1);
        const drawWidth = aspect >= 1 ? maxDrawSize : maxDrawSize * aspect;
        const drawHeight = aspect >= 1 ? maxDrawSize / aspect : maxDrawSize;
        return {
            drawWidth,
            drawHeight,
            spriteCenterY: -((drawHeight / 2) + (definition.yOffsetTiles * this.tileSize)),
        };
    }

    private buildAnimatedOverlayTransform(object: MapObjectSpriteInput): string {
        const transforms: string[] = [];
        const scaleX = object.flipHorizontal ? -1 : 1;
        const scaleY = object.flipVertical ? -1 : 1;
        if (scaleX !== 1 || scaleY !== 1) {
            transforms.push(`scale(${scaleX}, ${scaleY})`);
        }
        const rotationSteps = (((object.rotationSteps ?? 0) % 12) + 12) % 12;
        if (rotationSteps !== 0) {
            transforms.push(`rotate(${rotationSteps * 30}deg)`);
        }
        return transforms.join(' ');
    }

    private rebuildVisibleDirtyChunks(visibleChunks: readonly BoardChunk[]): void {
        for (const chunk of visibleChunks) {
            if (!this.dirtyChunkKeys.has(chunk.key)) {
                continue;
            }
            const texture = this.getOrCreateChunkTexture(chunk);
            const textureCtx = texture.getContext('2d');
            if (!textureCtx) {
                console.error('[StaticObjectLayerRenderer] Failed to create static object chunk context');
                continue;
            }
            textureCtx.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
            textureCtx.imageSmoothingEnabled = false;
            textureCtx.clearRect(0, 0, chunk.worldBounds.width, chunk.worldBounds.height);

            const objects = this.chunkObjects.get(chunk.key) ?? [];
            for (const object of objects) {
                const metrics = resolveMapObjectSpriteMetrics(object, this.tileSize);
                if (!metrics) {
                    continue;
                }
                const anchor = resolveMapObjectWorldAnchor(object, this.tileSize, this.grid);
                drawResolvedMapObjectSprite(
                    textureCtx,
                    metrics,
                    anchor.x - chunk.worldBounds.left,
                    anchor.y - chunk.worldBounds.top,
                    1,
                );
            }
            this.dirtyChunkKeys.delete(chunk.key);
        }
    }

    private getOrCreateChunkTexture(chunk: BoardChunk): HTMLCanvasElement {
        const existing = this.chunkTextures.get(chunk.key);
        const backingWidth = Math.max(1, Math.round(chunk.worldBounds.width * this.devicePixelRatio));
        const backingHeight = Math.max(1, Math.round(chunk.worldBounds.height * this.devicePixelRatio));
        if (existing && existing.width === backingWidth && existing.height === backingHeight) {
            return existing;
        }

        const canvas = document.createElement('canvas');
        canvas.width = backingWidth;
        canvas.height = backingHeight;
        this.chunkTextures.set(chunk.key, canvas);
        return canvas;
    }

    private collectChangedChunkKeys(
        previousObjects: readonly MapObjectSpriteInput[],
        nextObjects: readonly MapObjectSpriteInput[],
        previousGrid: GridGeometry
    ): Set<string> {
        const dirtyKeys = new Set<string>();
        const previousById = new Map(previousObjects.map((object) => [object.id, object]));
        const nextById = new Map(nextObjects.map((object) => [object.id, object]));
        const ids = new Set<string>([...previousById.keys(), ...nextById.keys()]);

        for (const id of ids) {
            const previous = previousById.get(id);
            const next = nextById.get(id);
            if (previous && next && this.objectSignature(previous) === this.objectSignature(next)) {
                continue;
            }

            if (previous) {
                for (const key of this.getObjectChunkKeys(previous, previousGrid)) {
                    dirtyKeys.add(key);
                }
            }
            if (next) {
                for (const key of this.getObjectChunkKeys(next, this.grid)) {
                    dirtyKeys.add(key);
                }
            }
        }

        return dirtyKeys;
    }

    private objectSignature(object: MapObjectSpriteInput): string {
        return [
            object.asset ?? '',
            object.x,
            object.y,
            object.offsetX ?? 0,
            object.offsetY ?? 0,
            object.sizeMultiplier ?? 1,
            object.flipHorizontal ? 1 : 0,
            object.flipVertical ? 1 : 0,
            object.rotationSteps ?? 0,
        ].join(':');
    }

    private getObjectChunkKeys(object: MapObjectSpriteInput, grid: GridGeometry): string[] {
        const bounds = this.resolveObjectWorldBounds(object, grid);
        const keys: string[] = [];
        const { cols, rows } = this.board.getChunkCount();
        for (let chunkY = 0; chunkY < rows; chunkY += 1) {
            for (let chunkX = 0; chunkX < cols; chunkX += 1) {
                const chunk = this.board.getChunk(chunkX, chunkY, 0);
                if (rectsIntersect(bounds, chunk.worldBounds)) {
                    keys.push(chunk.key);
                }
            }
        }
        return keys;
    }

    private resolveObjectWorldBounds(object: MapObjectSpriteInput, grid: GridGeometry): WorldRect {
        const anchor = resolveMapObjectWorldAnchor(object, this.tileSize, grid);
        const metrics = resolveMapObjectSpriteMetrics(object, this.tileSize);
        if (metrics) {
            const rotationRadians = metrics.rotationSteps * (Math.PI / 6);
            const halfWidth =
                Math.abs(Math.cos(rotationRadians)) * (metrics.drawWidth / 2) +
                Math.abs(Math.sin(rotationRadians)) * (metrics.drawHeight / 2);
            const halfHeight =
                Math.abs(Math.sin(rotationRadians)) * (metrics.drawWidth / 2) +
                Math.abs(Math.cos(rotationRadians)) * (metrics.drawHeight / 2);
            const centerY = anchor.y + metrics.spriteCenterY;
            return {
                left: anchor.x - halfWidth,
                top: centerY - halfHeight,
                right: anchor.x + halfWidth,
                bottom: centerY + halfHeight,
                width: halfWidth * 2,
                height: halfHeight * 2,
            };
        }

        const definition = getMapObjectVisualDefinition(object.asset ?? object.id);
        const drawScale = definition?.drawScale ?? 1;
        const drawSize = this.tileSize * drawScale * Math.max(0.01, object.sizeMultiplier ?? 1);
        const spriteCenterY = -((drawSize / 2) + ((definition?.yOffsetTiles ?? 0) * this.tileSize));
        const rotationSteps = (((object.rotationSteps ?? 0) % 12) + 12) % 12;
        const rotationRadians = rotationSteps * (Math.PI / 6);
        const rotatedHalfExtent =
            Math.abs(Math.cos(rotationRadians)) * (drawSize / 2) +
            Math.abs(Math.sin(rotationRadians)) * (drawSize / 2);
        const centerY = anchor.y + spriteCenterY;
        return {
            left: anchor.x - rotatedHalfExtent,
            top: centerY - rotatedHalfExtent,
            right: anchor.x + rotatedHalfExtent,
            bottom: centerY + rotatedHalfExtent,
            width: rotatedHalfExtent * 2,
            height: rotatedHalfExtent * 2,
        };
    }

    private invalidateAllChunks(): void {
        const { cols, rows } = this.board.getChunkCount();
        for (let chunkY = 0; chunkY < rows; chunkY += 1) {
            for (let chunkX = 0; chunkX < cols; chunkX += 1) {
                this.dirtyChunkKeys.add(this.board.getChunkKey(chunkX, chunkY));
            }
        }
        this.requestRender();
    }

    private invalidateChunkKeys(keys: Iterable<string>): void {
        let changed = false;
        for (const key of keys) {
            if (!this.dirtyChunkKeys.has(key)) {
                this.dirtyChunkKeys.add(key);
                changed = true;
            }
        }
        if (changed) {
            this.requestRender();
        }
    }
}
